import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCheck, FiMail, FiLoader, FiDownload, FiExternalLink, FiAlertTriangle, FiRefreshCw } = FiIcons

const POLL_INTERVAL_MS = 5000
const MAX_POLLS = 72 // 72 × 5s = 6 minutes before giving up

// Progress steps shown while Fal is working
const STEPS = [
  { phase: 'starting',            label: 'Submitting to AI…',               pct: 10 },
  { phase: 'upscaling',           label: 'Upscaling your signature (HD)…',   pct: 40 },
  { phase: 'removing_background', label: 'Creating transparent PNG…',        pct: 70 },
  { phase: 'ready',               label: 'Files ready!',                     pct: 100 },
]

function getStep(phase) {
  return STEPS.find((s) => s.phase === phase) || STEPS[0]
}

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const stripeSessionId = searchParams.get('session_id')

  // UI state
  const [uiPhase, setUiPhase] = useState('loading') // loading | starting | upscaling | removing_background | ready | error
  const [session, setSession] = useState(null)
  const [files, setFiles] = useState(null) // { final_jpg_url, final_png_url, final_svg_url }
  const [errorMsg, setErrorMsg] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  const triggerCalledRef = useRef(false)
  const pollTimerRef = useRef(null)
  const cancelledRef = useRef(false)
  const pollCountRef = useRef(0)

  // ── 1. Initial session load ─────────────────────────────────────────────
  const loadSession = useCallback(async () => {
    for (let attempt = 0; attempt < 15; attempt++) {
      if (cancelledRef.current) return
      try {
        const res = await fetch(`/api/stripe/get-session?stripe_session_id=${stripeSessionId}`)
        if (res.status === 404) {
          await new Promise((r) => setTimeout(r, 800))
          continue
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || 'Could not load your order')
        }
        return await res.json()
      } catch (e) {
        if (attempt === 14) throw e
        await new Promise((r) => setTimeout(r, 800))
      }
    }
    return null
  }, [stripeSessionId])

  // ── 2. Trigger processing (called once) ────────────────────────────────
  const triggerProcessing = useCallback(async () => {
    try {
      const res = await fetch('/api/stripe/trigger-processing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_session_id: stripeSessionId }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        console.error('trigger-processing failed:', res.status, body)
      }
    } catch (e) {
      console.error('trigger-processing fetch error:', e)
    }
  }, [stripeSessionId])

  // ── 3. Poll for pipeline progress ──────────────────────────────────────
  const poll = useCallback(async () => {
    if (cancelledRef.current) return

    pollCountRef.current += 1

    // Hard browser-side timeout — prevents infinite loops if server never errors
    if (pollCountRef.current > MAX_POLLS) {
      setErrorMsg(
        'Processing is taking longer than expected. Click "Try again" to restart — your payment is safe.'
      )
      setUiPhase('error')
      return
    }

    try {
      const res = await fetch('/api/stripe/poll-processing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_session_id: stripeSessionId }),
      })
      if (!res.ok) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
        return
      }
      const data = await res.json()

      if (data.status === 'completed') {
        setFiles({
          final_jpg_url: data.final_jpg_url,
          final_png_url: data.final_png_url,
          final_svg_url: data.final_svg_url,
        })
        setUiPhase('ready')
        return
      }

      if (data.status === 'error') {
        setErrorMsg(
          data.error?.includes('retry')
            ? 'Processing stalled. Click "Try again" to restart — your payment is safe and we will not charge you again.'
            : 'Something went wrong generating your files. Please contact support.'
        )
        setUiPhase('error')
        return
      }

      const phase = data.phase || 'starting'
      setUiPhase((prev) => (prev === 'ready' || prev === 'error' ? prev : phase))
    } catch (e) {
      console.error('poll-processing error:', e)
    }
    if (!cancelledRef.current) {
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
    }
  }, [stripeSessionId])

  // ── Main effect ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!stripeSessionId) {
      setErrorMsg('No session ID found. Please check your payment confirmation email.')
      setUiPhase('error')
      return
    }

    cancelledRef.current = false
    pollCountRef.current = 0

    const run = async () => {
      try {
        const data = await loadSession()
        if (cancelledRef.current) return

        if (!data) {
          setErrorMsg('We could not find your order. If you just paid, wait a moment and refresh.')
          setUiPhase('error')
          return
        }

        setSession(data)

        // If already completed (e.g. user refreshed)
        if (data.status === 'completed' && data.final_jpg_url) {
          setFiles({
            final_jpg_url: data.final_jpg_url,
            final_png_url: data.final_png_url,
            final_svg_url: data.final_svg_url,
          })
          setUiPhase('ready')
          return
        }

        if (data.status === 'error') {
          setErrorMsg('There was a problem generating your files. Please contact support.')
          setUiPhase('error')
          return
        }

        // Not ready — trigger + start polling
        setUiPhase('starting')

        if (!triggerCalledRef.current) {
          triggerCalledRef.current = true
          await triggerProcessing()
        }

        if (!cancelledRef.current) {
          pollTimerRef.current = setTimeout(poll, 500)
        }
      } catch (e) {
        if (!cancelledRef.current) {
          setErrorMsg(e.message || 'Something went wrong. Please refresh.')
          setUiPhase('error')
        }
      }
    }

    run()

    return () => {
      cancelledRef.current = true
      clearTimeout(pollTimerRef.current)
    }
  }, [stripeSessionId, retryCount, loadSession, triggerProcessing, poll])

  const handleRetry = () => {
    triggerCalledRef.current = false
    pollCountRef.current = 0
    setUiPhase('loading')
    setErrorMsg(null)
    setFiles(null)
    setRetryCount((c) => c + 1)
  }

  const fileBase = session?.name?.replace(/[^\w-]+/g, '_') || 'signature'

  // ── Loading ──────────────────────────────────────────────────────────────
  if (uiPhase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="text-5xl text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Confirming your payment…</p>
        </div>
      </div>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (uiPhase === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
          <SafeIcon icon={FiAlertTriangle} className="text-4xl text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
          <p className="text-slate-600 mb-6 text-sm leading-relaxed">{errorMsg}</p>
          <div className="flex gap-3">
            <button
              onClick={handleRetry}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="text-lg" />
              Try again
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 border-2 border-slate-200 text-slate-700 px-4 py-3 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isProcessing = uiPhase !== 'ready'
  const step = getStep(uiPhase)

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="rounded-2xl shadow-xl overflow-hidden border border-slate-200/80 bg-white">

          {/* Header */}
          <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 px-6 py-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
              <SafeIcon icon={FiCheck} className="text-4xl text-white" />
            </div>
            <p className="text-indigo-100 text-sm font-medium tracking-wide uppercase mb-1">Thank you</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              {session?.name ? `${session.name}, your payment went through` : 'Your payment was successful'}
            </h1>
          </div>

          <div className="p-6 sm:p-8 space-y-6">

            {/* Email notification */}
            {session?.customer_email && (
              <div className="flex gap-3 rounded-xl bg-slate-50 border border-slate-100 p-4">
                <SafeIcon icon={FiMail} className="text-xl text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Download links sent by email</p>
                  <p className="text-slate-500 text-sm mt-0.5">
                    A copy will be emailed to{' '}
                    <span className="font-medium text-slate-700 break-all">{session.customer_email}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Progress bar (while processing) */}
            {isProcessing && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <SafeIcon icon={FiLoader} className="text-indigo-500 animate-spin" />
                    {step.label}
                  </span>
                  <span className="text-xs text-slate-400 font-medium">{step.pct}%</span>
                </div>
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${step.pct}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-400 pt-1">
                  {STEPS.slice(0, -1).map((s) => (
                    <span
                      key={s.phase}
                      className={step.pct >= s.pct ? 'text-indigo-600 font-medium' : ''}
                    >
                      {s.label.split('…')[0]}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-slate-400 text-center pt-1">
                  Usually takes 1–3 minutes. Keep this page open.
                </p>
              </div>
            )}

            {/* Download links (when ready) */}
            {uiPhase === 'ready' && files && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
                    <SafeIcon icon={FiCheck} className="text-2xl text-emerald-600" />
                  </div>
                  <p className="font-bold text-slate-900 text-lg">Your files are ready!</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Click below to open. Please save each file to your device for future access.
                  </p>
                </div>

                <div className="grid gap-3">
                  <a
                    href={files.final_jpg_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-600/20"
                  >
                    <span className="flex items-center gap-2">
                      <SafeIcon icon={FiDownload} className="text-lg" />
                      Download JPG
                    </span>
                    <span className="text-xs opacity-70 font-normal">Black background · HD</span>
                  </a>

                  <a
                    href={files.final_png_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors shadow-md shadow-teal-600/20"
                  >
                    <span className="flex items-center gap-2">
                      <SafeIcon icon={FiDownload} className="text-lg" />
                      Download PNG
                    </span>
                    <span className="text-xs opacity-70 font-normal">Transparent background</span>
                  </a>

                  <a
                    href={files.final_svg_url}
                    download={`${fileBase}-signature.svg`}
                    className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition-colors shadow-md shadow-violet-600/20"
                  >
                    <span className="flex items-center gap-2">
                      <SafeIcon icon={FiDownload} className="text-lg" />
                      Download SVG
                    </span>
                    <span className="text-xs opacity-70 font-normal">Vector · scalable</span>
                  </a>
                </div>

                <p className="text-center text-xs text-slate-400 pt-1">
                  Links are valid for approximately 1 hour.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess
